import PropTypes from 'prop-types'

import {List, Map} from 'immutable'
import {useState, useMemo} from 'react'
import {connect} from 'react-redux'
import {createResource, readEndpoint} from 'redux-json-api'

import {useApolloClient, useMutation} from '@apollo/client'

import AddDeputyStepOne from './AddDeputyStepOne'
import AddDeputyStepThree from './AddDeputyStepThree'
import AddDeputyStepTwo from './AddDeputyStepTwo'

import {CONTACTS} from '../../../../../graphql/queries/userData'
import {CREATE_DEPUTY} from '../../../../../graphql/mutations/deputy'
import {DEPUTIES, SPECIFIC_ITEMS_DEPUTIES} from '../../../../../graphql/queries/deputy'

import {
  filterRawResourceByAttributeId,
  findFirmConfigByFirmOwnership,
  findRawResourceByAttributeId,
  findRawResourceById
} from '../../../../../lib/plan_data/userData'

import {
  formatDeputyPermissions
} from '../../../../../lib/deputyPermissionTools'

import {trackAddDeputyEvent} from './addDeputyEventLogger'

import './addDeputyController.scss'

export const AddDeputyController = props => {
  const [step, setStep] = useState(1)

  const everplanHasResponses = useMemo(
    () => props.itemResponses.some(itemResponse => !itemResponse.get('response-groups').isEmpty()),
    [props.itemResponses]
  )

  const logMixpanelEvent = eventName => (
    trackAddDeputyEvent({context: props.context, eventName})
  )

  const everplanId = props.userConfig.get('everplan-id')

  const apolloClient = useApolloClient()

  const [createDeputy] = useMutation(CREATE_DEPUTY)

  // This scrolls the modal to the top when moving in between the screens
  const scrollToTop = () => { document.getElementsByClassName('add-deputy-controller')[0].scrollTop = 0 }

  const goBack = event => {
    if (event)
      event.preventDefault()

    // Reset the permissions if they are going back from step 2 to step one
    //  except for draft Deputies
    if (step === 2 && !props.isDraftDeputy)
      props.setDeputy({...props.deputy, permissions: null})

    setStep(step - 1)
    scrollToTop()
  }

  const saveAndGoToNextStep = (newDeputyData = {}) => {
    props.setDeputy({...props.deputy, ...newDeputyData})
    setStep(step + 1)
    scrollToTop()
  }

  const updateDeputy = (newDeputyData = {}) => { props.setDeputy({...props.deputy, ...newDeputyData}) }

  const sharedProps = {
    ...props,
    goBack,
    logMixpanelEvent,
    nextStep: saveAndGoToNextStep,
    updateDeputy
  }

  const handleSubmitForQuarterback = () => {
    const {clientEverplanId, deputy} = props

    const payload = {
      attributes: {
        'everplan-id': clientEverplanId,
        'custom-invite-text': deputy.inviteText,
        email: deputy.email,
        'first-name': deputy['first-name'],
        'last-name': deputy['last-name'],
        'deputy-config': {
          'death-reporter': deputy['death-reporter'],
          'death-verification-period': deputy['death-verification-period'],
          'deputy-relationship': deputy['deputy-relationship'].value
        },
        'permissions-config': deputy.permissions ?
          Object.fromEntries(Object.entries(deputy.permissions).filter(
            ([key]) => key !== 'selectAll')) :
          {}
      },
      type: 'draft-deputies'
    }

    props.createResource(payload)
      .then(() => {
        apolloClient.refetchQueries({
          include: [
            SPECIFIC_ITEMS_DEPUTIES,
            {
              query: CONTACTS,
              variables: {everplanId: Number(clientEverplanId)}
            }
          ]
        })

        props.readEndpoint(
          `client-seats/${props.clientSeatId}?include=everplan-summary`
        )

        props.handleSubmitCompleted()
      })
  }

  const submit = () => {
    const {deputy} = props

    const payload = {
      deathReporter: deputy['death-reporter'],
      deathVerificationPeriod: deputy['death-verification-period'],
      deputyRelationship: deputy['deputy-relationship'].value,
      deputyInvite: {
        firstName: deputy['first-name'],
        lastName: deputy['last-name'],
        email: deputy.email,
        customInviteText: deputy.inviteText
      },
      permissions: deputy.permissions ? formatDeputyPermissions(deputy.permissions, props.vaultExtension) : []
    }

    createDeputy({
      variables: {input: payload},
      onCompleted: () => {
        props.handleSubmitCompleted(() => {
          apolloClient.refetchQueries({
            include: [
              {query: DEPUTIES, variables: {everplanId}},
              SPECIFIC_ITEMS_DEPUTIES,
              {query: CONTACTS, variables: {everplanId}}
            ]
          })
        })
      }
    })
  }

  const handleSubmit = () => {
    if (props.context === 'pro_client_dashboard')
      handleSubmitForQuarterback()
    else
      submit()
  }

  const renderStep = () => {
    const shouldShowPermissionsStep = everplanHasResponses || !props.vaultExtension.isEmpty()
    const shouldRenderPermissionsStep = shouldShowPermissionsStep && (
      step === 2 || !(props.deputy && props.deputy.permissions)
    )

    if (step === 1)
      return <AddDeputyStepOne {...sharedProps} />
    else if (shouldRenderPermissionsStep)
      return <AddDeputyStepTwo {...sharedProps} everplanHasResponses={everplanHasResponses} />
    else
      return <AddDeputyStepThree {...sharedProps} submit={handleSubmit} />
  }


  return (
    <div className='add-deputy-controller'>{renderStep()}</div>
  )
}

AddDeputyController.propTypes = {
  clientEverplanId: PropTypes.string,
  clientSeatId: PropTypes.number,
  context: PropTypes.string.isRequired,
  createResource: PropTypes.func,
  deputies: PropTypes.instanceOf(List),
  deputy: PropTypes.object,
  handleSubmitCompleted: PropTypes.func,
  isDraftDeputy: PropTypes.bool,
  itemResponses: PropTypes.instanceOf(List),
  readEndpoint: PropTypes.func.isRequired,
  setDeputy: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map),
  vaultExtension: PropTypes.instanceOf(Map)
}

const mapStateToProps = (state, {userConfig, clientEverplanId}) => {
  const categories = state.categories

  const firmOwnership = findRawResourceByAttributeId({
    rawResource: state.api['firm-ownerships'],
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id')
  })

  const firm = findRawResourceById({
    rawResource: state.api.firms,
    id: firmOwnership.get('ownable-id')
  })

  const firmConfig = findFirmConfigByFirmOwnership({
    firmOwnerships: state.api['firm-ownerships'],
    firmConfigs: state.api['firm-configs'],
    userConfig
  })

  const itemViews = filterRawResourceByAttributeId({
    attribute: 'everplan-id',
    id: userConfig.get('everplan-id'),
    rawResource: state.api['item-views']
  })

  const vaultExtension = findRawResourceByAttributeId({
    rawResource: state.api['vault-extensions'],
    attribute: 'everplan-id',
    id: clientEverplanId ? clientEverplanId : userConfig.get('everplan-id')
  })

  return {categories, firm, firmConfig, itemViews, vaultExtension}
}


export default connect(mapStateToProps, {createResource, readEndpoint})(AddDeputyController)
